// Drive MediaRecorder over the composite stream. Hands each timeslice
// blob to IndexedDB, tagged with an incrementing seq number per session.
// Upload is a separate concern (5.10.4) — this module just records and
// buffers durably.

import { ensureOpen, putChunk } from './idb';
import { startComposite, type ComposerHandle, type ComposerLabels } from './composer';
import { pumpSession, uploadThumbnail } from './uploader';

export interface RecordingHandle {
  /** Stop recording. Resolves once the trailing chunk is flushed to IDB. */
  stop: () => Promise<void>;
  /** Inspect the current sequence position — useful for logging. */
  getSeq: () => number;
  /** Route the agent's screen-share into the recording. Pass the
   *  getDisplayMedia stream on share start (promotes it to the main frame),
   *  null on stop. No-op once the recorder has been torn down. */
  setScreen: (stream: MediaStream | null) => void;
  /** Mark whether the VISITOR is screen-sharing (drives the on-frame
   *  caption only — their screen already arrives on the remote track). */
  setRemoteSharing: (sharing: boolean) => void;
}

// Chunk interval — small enough that a crash loses ≤TIMESLICE_MS, big
// enough that we don't murder IDB with tiny writes.
const TIMESLICE_MS = 5000;
// Anything below this is almost certainly a malformed mid-teardown blob
// (e.g. a 1-byte header fragment we observed in production after a
// race-condition cleanup). Real first chunks weigh in at hundreds of KB
// even for very short calls.
const MIN_CHUNK_BYTES = 64;

const PREFERRED_MIME = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

function pickMime(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  for (const m of PREFERRED_MIME) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return '';
}

export interface RecorderOptions {
  sessionId: string;
  remote: MediaStream;
  local: MediaStream;
  /** Optional — called once each chunk is durably written to IDB. */
  onChunk?: (seq: number) => void;
  /** Optional — called when MediaRecorder errors out fatally. */
  onError?: (err: unknown) => void;
  /** Display names for the "whose screen" caption on shared-screen frames. */
  labels?: ComposerLabels;
}

export function startRecording(opts: RecorderOptions): RecordingHandle | null {
  if (typeof MediaRecorder === 'undefined') return null;
  const mime = pickMime();
  if (!mime) return null;

  let composite: ComposerHandle | null = null;
  let recorder: MediaRecorder;
  let seq = 0;
  let stopped = false;
  // One unique call_id per startRecording() invocation. A single session can
  // host multiple calls — each gets its own bucket of chunks (seq restarts
  // at 0 per call). UUID lets the server group + replay them independently.
  const callId = crypto.randomUUID();
  const callStartedAt = Date.now();

  try {
    composite = startComposite(opts.remote, opts.local, opts.labels);
    recorder = new MediaRecorder(composite.stream, {
      mimeType: mime,
      videoBitsPerSecond: 1_500_000,
      audioBitsPerSecond: 96_000,
    });
  } catch (err) {
    composite?.stop();
    opts.onError?.(err);
    return null;
  }

  // Track every in-flight `putChunk` Promise so stop() can wait for them
  // all to commit before resolving. The old "50 ms after onstop" hack
  // failed for short calls where IDB's first-open upgrade took longer
  // than the grace window and the final chunk got lost.
  let pendingChunks = 0;
  let allFlushed: (() => void) | null = null;
  function chunkSettled() {
    pendingChunks--;
    if (pendingChunks === 0 && allFlushed) {
      const fn = allFlushed;
      allFlushed = null;
      fn();
    }
  }

  recorder.ondataavailable = (e) => {
    const size = e.data?.size ?? 0;
    if (size < MIN_CHUNK_BYTES) {
      if (size > 0) {
         
        console.warn('[recording] dropping tiny chunk', { size, callId });
      }
      return;
    }
    const mySeq = seq++;
    pendingChunks++;
    void (async () => {
      try {
        await putChunk({
          sessionId: opts.sessionId,
          callId,
          seq: mySeq,
          blob: e.data,
          recordedAt: Date.now(),
          callStartedAt,
        });
        opts.onChunk?.(mySeq);
        // Fire-and-forget upload. Best-effort — failures retry on next
        // chunk or via resumeOrphans() at app boot.
        void pumpSession(opts.sessionId);
      } catch (err) {
        // IDB write failure is non-fatal — the call continues, but this
        // chunk is lost. Surface for diagnostics.
         
        console.warn('[recording] failed to persist chunk', mySeq, err);
      } finally {
        chunkSettled();
      }
    })();
  };
  recorder.onerror = (e) => opts.onError?.(e);

  // Warm up IDB BEFORE starting the recorder so the first chunk's
  // putChunk doesn't pay the db-upgrade tax. This was the silent killer
  // for short calls — the upgrade transaction could take 100-200 ms,
  // long enough that stop()'s flush window expired before the chunk hit
  // disk.
  void ensureOpen();

  recorder.start(TIMESLICE_MS);

  // Capture + upload a thumbnail ~2s into the call. We delay so the canvas
  // has live frames (camera + remote feed both flowing) rather than a
  // gray placeholder. Best-effort — a failed upload doesn't affect the
  // recording itself; the row just won't have a thumbnail.
  window.setTimeout(() => {
    if (stopped || !composite) return;
    void composite.snapshot().then((blob) => {
      if (!blob || stopped) return;
      void uploadThumbnail(opts.sessionId, callId, blob);
    });
  }, 2000);

  async function stop(): Promise<void> {
    if (stopped) return;
    stopped = true;
    // 1. Wait for MediaRecorder to fire its 'stop' event. Per spec, the
    //    browser fires ondataavailable with the trailing buffer THEN
    //    fires onstop, so by the time this resolves the final chunk's
    //    handler has already entered our pendingChunks counter.
    const stopFired = new Promise<void>((resolve) => {
      recorder.addEventListener('stop', () => resolve(), { once: true });
    });
    try {
      if (recorder.state !== 'inactive') recorder.stop();
    } catch {
      /* already stopped */
    }
    await stopFired;

    // 2. Wait for every pending putChunk Promise (including any fired
    //    from the trailing ondataavailable) to commit to IDB. Without
    //    this the next step's pumpSession may not find the new chunk.
    if (pendingChunks > 0) {
      await new Promise<void>((resolve) => {
        allFlushed = resolve;
      });
    }

    // 3. Tear down the composite once all chunks are durably in IDB.
    composite?.stop();
    composite = null;

    if (seq === 0) {
      // Hard signal that the call produced nothing recordable. Surfaces
      // in DevTools so we can correlate with on-disk state next time.
       
      console.warn('[recording] call produced no chunks', {
        callId,
        sessionId: opts.sessionId,
      });
    }

    // 4. One last awaited upload sweep so callers can rely on stop()
    //    completing with chunks attempted (not still pending). Failures
    //    are absorbed by pumpSession's inner try/catch — bus reconnect
    //    or resumeOrphans() picks up the slack.
    try {
      await pumpSession(opts.sessionId);
    } catch {
      /* defensive — pumpSession's own callers swallow errors. */
    }
  }

  return {
    stop,
    getSeq: () => seq,
    setScreen: (stream) => composite?.setScreen(stream),
    setRemoteSharing: (sharing) => composite?.setRemoteSharing(sharing),
  };
}
